// Selection bounding box with 8 resize handles + a rotation handle.
// All overlay geometry is rendered in artboard coordinates and rotated to
// match the element, so resize/stretch and rotation work together correctly.
import React, { useRef } from "react";
import {
  bboxOf,
  resizeElement,
  translateElement,
  rotateVec,
  rotatePoint,
  centerOf,
  rotationOf,
} from "./canvasUtils";

const HANDLES = [
  { k: "nw", x: 0, y: 0 },
  { k: "n", x: 0.5, y: 0 },
  { k: "ne", x: 1, y: 0 },
  { k: "e", x: 1, y: 0.5 },
  { k: "se", x: 1, y: 1 },
  { k: "s", x: 0.5, y: 1 },
  { k: "sw", x: 0, y: 1 },
  { k: "w", x: 0, y: 0.5 },
];

export default function SelectionOverlay({
  element,
  scale = 1,
  getSvgPoint,
  onLiveUpdate,
  onResizeStart,
  onResizeEnd,
}) {
  const dragRef = useRef(null);
  const bb = bboxOf(element);
  if (bb.w === 0 && bb.h === 0) return null;

  const deg = rotationOf(element);
  const c = centerOf(bb);

  const handleSize = 9 / scale;
  const padding = 4 / scale;
  const x = bb.x - padding;
  const y = bb.y - padding;
  const w = bb.w + padding * 2;
  const h = bb.h + padding * 2;
  const rotorLen = 26 / scale;
  const rotorX = x + w / 2;
  const rotorY = y - rotorLen;

  const onHandlePointerDown = (e, k) => {
    e.stopPropagation();
    e.preventDefault();
    const startPt = getSvgPoint(e.clientX, e.clientY);
    onResizeStart?.();
    e.target.setPointerCapture?.(e.pointerId);
    const original = element;
    const b = bb;
    const C0 = centerOf(b);
    dragRef.current = true;

    const onMove = (ev) => {
      if (!dragRef.current) return;
      const p = getSvgPoint(ev.clientX, ev.clientY);
      // Map the screen-space drag delta into the element's un-rotated frame.
      const local = rotateVec({ x: p.x - startPt.x, y: p.y - startPt.y }, -deg);
      const dx = local.x;
      const dy = local.y;

      // Scale factors may go NEGATIVE: when a handle is dragged past the
      // opposite side the shape flips (reverse-drawing). resizeElement
      // normalizes the geometry (min/abs) so coordinates swap automatically.
      let sx = 1;
      let sy = 1;
      if (k.includes("e")) sx = b.w === 0 ? 1 : (b.w + dx) / b.w;
      if (k.includes("w")) sx = b.w === 0 ? 1 : (b.w - dx) / b.w;
      if (k.includes("s")) sy = b.h === 0 ? 1 : (b.h + dy) / b.h;
      if (k.includes("n")) sy = b.h === 0 ? 1 : (b.h - dy) / b.h;

      // Origin = opposite corner / edge (stays fixed in local space).
      let ox = b.x;
      let oy = b.y;
      if (k.includes("w")) ox = b.x + b.w;
      if (k.includes("n")) oy = b.y + b.h;

      let attrs = resizeElement(original, original, sx, sy, ox, oy);
      // Resize may change the element type (circle -> ellipse on non-uniform
      // scaling). __type carries that decision; strip it before storing.
      const newType = attrs.__type;
      if (newType) delete attrs.__type;
      const effType = newType || original.type;

      // When rotated, keep the opposite corner pinned in screen space by
      // compensating for the bbox-center shift the resize introduces.
      if (deg) {
        const oppLocal = { x: ox, y: oy };
        const sAnchor = rotatePoint(oppLocal, C0, deg);
        const C1 = centerOf(bboxOf({ ...original, type: effType, attrs }));
        const sAnchorNew = rotatePoint(oppLocal, C1, deg);
        const D = { x: sAnchor.x - sAnchorNew.x, y: sAnchor.y - sAnchorNew.y };
        attrs = translateElement({ ...original, type: effType, attrs }, D.x, D.y);
      }
      onLiveUpdate(original.id, attrs, newType);
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      onResizeEnd?.();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const onRotatePointerDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onResizeStart?.();
    e.target.setPointerCapture?.(e.pointerId);
    const original = element;
    const C0 = centerOf(bboxOf(original));
    const startPt = getSvgPoint(e.clientX, e.clientY);
    const startAng = Math.atan2(startPt.y - C0.y, startPt.x - C0.x);
    const startDeg = deg;

    const onMove = (ev) => {
      const p = getSvgPoint(ev.clientX, ev.clientY);
      const ang = Math.atan2(p.y - C0.y, p.x - C0.x);
      let nd = startDeg + ((ang - startAng) * 180) / Math.PI;
      if (ev.shiftKey) nd = Math.round(nd / 15) * 15;
      nd = Math.round((((nd % 360) + 360) % 360) * 10) / 10;
      onLiveUpdate(original.id, { ...original.attrs, rotation: nd });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      onResizeEnd?.();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const cursorOf = (k) => {
    const map = { n: "ns", s: "ns", e: "ew", w: "ew", nw: "nwse", se: "nwse", ne: "nesw", sw: "nesw" };
    return `${map[k]}-resize`;
  };

  return (
    <g
      transform={deg ? `rotate(${deg} ${c.x} ${c.y})` : undefined}
      pointerEvents="auto"
      data-testid="selection-overlay"
    >
      <rect x={x} y={y} width={w} height={h} className="selection-box" />
      <line x1={rotorX} y1={y} x2={rotorX} y2={rotorY} className="selection-box" />
      <circle
        cx={rotorX}
        cy={rotorY}
        r={handleSize / 1.5}
        className="selection-handle rotate"
        style={{ cursor: "grab" }}
        onPointerDown={onRotatePointerDown}
        data-testid="rotate-handle"
      />
      {HANDLES.map((h2) => {
        const hx = x + w * h2.x - handleSize / 2;
        const hy = y + h * h2.y - handleSize / 2;
        return (
          <rect
            key={h2.k}
            x={hx}
            y={hy}
            width={handleSize}
            height={handleSize}
            className="selection-handle"
            style={{ cursor: cursorOf(h2.k) }}
            onPointerDown={(e) => onHandlePointerDown(e, h2.k)}
            data-testid={`resize-handle-${h2.k}`}
          />
        );
      })}
    </g>
  );
}
